/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { FC, useCallback, useMemo } from "react";
import { IFieldValidationError } from "./FieldTypes";
import { FieldWrapper } from "./FieldWrapper";
import { BEM, someIncludesCaseInsensitive } from "@adapptio/lib-components";
import { useUuid } from "@adapptio/lib-components/src/Hooks/useUuid";
import { useDeepCompareCallback } from "use-deep-compare";
import { Select } from "../Select/Select";
import { SelectItem } from "../Select/SelectItem";
import { SelectSeparator } from "../Select/SelectSeparator";
import { SelectItemIcon } from "../Select/SelectItemIcon";
import { SelectItemText } from "../Select/SelectItemText";
import { SelectEmpty } from "../Select/SelectEmpty";
import { InputAdornment } from "../InputAdornment";
import { FieldValidationErrors } from "./FieldValidationErrors";

export interface IFieldDropdownOption {
	label?: string;
	value?: string;
	description?: string;
	icon?: string;
}

export interface IFieldDropdownOption_Item {
	value: string;
	label: string;
	description?: string;
	icon?: string;
}

export interface IFieldDropdownOption_Separator {
	isSeparator: true;
}

export type TFieldDropdownOption = IFieldDropdownOption_Item | IFieldDropdownOption_Separator;

function isOptionSeparator(
	value: IFieldDropdownOption_Item | IFieldDropdownOption_Separator
): value is IFieldDropdownOption_Separator {
	return (value as IFieldDropdownOption_Separator)?.isSeparator;
}

function isOptionItem(
	value: IFieldDropdownOption_Item | IFieldDropdownOption_Separator
): value is IFieldDropdownOption_Item {
	return typeof value === "object" && !isOptionSeparator(value);
}

export interface IFieldDropdownProps {
	id?: string;
	label?: string;
	value: string;
	options: Array<TFieldDropdownOption>;
	placeholder?: string;
	readOnly?: boolean;
	allowCustomInput?: boolean;
	validationErrors?: IFieldValidationError[];
	className?: string;
	onChange?: (value: string) => void;
	grow?: boolean;
}

export const FieldDropdown: FC<IFieldDropdownProps> = ({
	id: propId,
	value,
	placeholder,
	readOnly,
	options,
	allowCustomInput,
	onChange,
	validationErrors,
	className,
	grow
}) => {
	const generatedId = useUuid();
	const id = propId || generatedId;

	const baseClass = BEM("edt-field-control");

	const selectedOptionIndex = useMemo(
		() => (value ? options.findIndex((option) => isOptionItem(option) && option.value === value) : -1),
		[ value ]
	);

	const getOptionId = useCallback((option: IFieldDropdownOption) => `${id}-option-${option.value}`, [ id ]);

	const selectedOption: IFieldDropdownOption_Item =
		selectedOptionIndex > -1 && isOptionItem(options[selectedOptionIndex])
			? (options[selectedOptionIndex] as IFieldDropdownOption_Item)
			: null;

	const filterOptions = useDeepCompareCallback<(value: string) => TFieldDropdownOption[]>(
		(value) => {
			const filtered = options.reduce((filtered, option, index) => {
				if (
					isOptionSeparator(option) &&
					!isOptionSeparator(filtered[filtered.length - 1]) &&
					index !== options.length - 1
				) {
					return [ ...filtered, option ];
				}

				if (isOptionItem(option)) {
					if (someIncludesCaseInsensitive(value, [ option.label, option.value ])) {
						return [ ...filtered, option ];
					}
				}

				return filtered;
			}, []);

			while (isOptionSeparator(filtered[0])) {
				filtered.shift();
			}

			while (isOptionSeparator(filtered[filtered.length - 1])) {
				filtered.pop();
			}

			return filtered;
		},
		[ options ]
	);

	const filteredOptions = useMemo(
		() => (!selectedOption && value ? filterOptions(value) : options),
		[ options, value, selectedOption, filterOptions ]
	);

	return (
		<FieldWrapper type="dropdown" className={className} grow={grow}>
			<Select
				className={baseClass.toClassName()}
				value={value}
				readOnly={readOnly}
				id={id}
				allowCustomInput={allowCustomInput}
				placeholder={placeholder}
				onChange={onChange}
				displayedValue={selectedOption?.label || selectedOption?.value || value}
				displayedIcon={selectedOption?.icon || null}
				endAdornment={
					validationErrors?.length > 0 && (
						<InputAdornment>
							<FieldValidationErrors errors={validationErrors} />{" "}
						</InputAdornment>
					)
				}
			>
				{filteredOptions?.length > 0 ? (
					filteredOptions.map((option, index) =>
						isOptionItem(option) ? (
							<SelectItem
								key={getOptionId(option)}
								id={getOptionId(option)}
								value={option.value}
							>
								{option.icon && <SelectItemIcon name={option.icon} />}
								<SelectItemText
									primary={option.label || option.value}
									secondary={option.description}
								/>
							</SelectItem>
						) : (
							<SelectSeparator key={index} />
						)
					)
				) : (
					<SelectEmpty className={baseClass.element("empty").toClassName()}>
						No items found
					</SelectEmpty>
				)}
			</Select>
		</FieldWrapper>
	);
};
